/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {Flex, Stack, Text} from '@chakra-ui/react'
import {useMaterialVariation} from '../../hooks/use-variation-attributes'
import {useItemVariant} from './'
import {useCurrency} from '../../hooks'
import {useCurrentCustomer} from '../../hooks/use-current-customer'

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({includeQuantity, currency, hidePromotionDetails, ...props}) => {
    const variant = useItemVariant()
    const {data: basket} = useCurrentBasket()
    const {currency: activeCurrency} = useCurrency()
    const {c_engraving: engravingText} = variant

    const materialVariation = useMaterialVariation(variant)

    const {isCustomNetPriceEnabled} = useCurrentCustomer().data

    // Create a mapping of variation values to their associated attributes. This allows us
    // the render the readable names/labels rather than variation value IDs.
    const variationValues = [
        // include material variation value if it exists
        materialVariation
            ? {
                  id: materialVariation.id,
                  name: materialVariation.name,
                  value: materialVariation.selectedValue.name
              }
            : null,
        ...Object.entries(variant.variationValues || {}).map(([key, value]) => {
            const attr = variant?.variationAttributes?.find((attr) => attr.id === key)
            return {
                id: key,
                name: attr?.name || key,
                value: attr.values.find((val) => val.value === value)?.name || value
            }
        })
    ]
        // filter out null values
        .filter(Boolean)

    return (
        <Stack spacing={2} flex={1} {...props} textStyle="Regular Small">
            {variationValues?.map((variationValue) => (
                <Text key={variationValue.id}>
                    {variationValue.name}: {variationValue.value}
                </Text>
            ))}

            {includeQuantity && (
                <Text>
                    <FormattedMessage
                        defaultMessage="Quantity: {quantity}"
                        values={{quantity: variant.quantity}}
                        id="item_attributes.label.quantity"
                    />
                </Text>
            )}

            {engravingText && (
                <Text>
                    <FormattedMessage
                        defaultMessage="Text: {engraving}"
                        values={{engraving: engravingText.replaceAll('||', ', ')}}
                        id="item_attributes.label.engraving"
                    />
                </Text>
            )}

            {isCustomNetPriceEnabled &&
                variant.priceAdjustments?.length > 0 &&
                !variant.bonusProductLineItem &&
                !hidePromotionDetails && (
                    <Flex alignItems="center">
                        <Text>
                            <FormattedMessage
                                defaultMessage="Promotions"
                                id="item_attributes.label.promotions"
                            />
                            {': '}
                            <Text as="span" color="stokkeFunctional.alertGreen">
                                {isCustomNetPriceEnabled ? (
                                    <FormattedNumber
                                        style="currency"
                                        currency={currency || basket?.currency || activeCurrency}
                                        value={variant.c_productLevelNetPriceAdjustment}
                                    />
                                ) : (
                                    <FormattedNumber
                                        style="currency"
                                        currency={currency || basket?.currency || activeCurrency}
                                        value={variant.c_productLevelPriceAdjustment}
                                    />
                                )}
                            </Text>
                        </Text>
                    </Flex>
                )}
        </Stack>
    )
}

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    currency: PropTypes.string,
    hidePromotionDetails: PropTypes.bool
}

export default ItemAttributes
